import { KeyFindingsTable } from "./KeyFindingsTable";
import { IncentivePointsSection } from "./IncentivePointsSection";
import { SharePermissionsSection } from "./SharePermissionsSection";
import { FileDescriptionSection } from "@/react/components/entityViews/files/components/editors/FileDetailModal/components/FileDescriptionSection";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import FilePreview from "@/modern/components/file-preview";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { getFileExtension } from "@/modern/utils";
import { DateHelper } from "@bryntum/schedulerpro";
import { FileText, Image as ImageIcon, Calendar, User, Clock, HardDrive, Database, MapPin, Award, Share2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";

export const SummaryTab = () => {
  const { file, displayedImageUrl, currentImageUrl, handleLoadComplete, summaryTab, setSummaryTab } = useFileDetailModal();

  if (!file) return null;

  const isPDF = getFileExtension(file.name) === "pdf";
  const isImage = isImageFileTyped(file) && !isPDF;

  // For first load when displayedImageUrl is undefined, use currentImageUrl
  // After first load, always use displayedImageUrl to show previous image while new one loads
  const srcToDisplay = displayedImageUrl || (isImage ? currentImageUrl : undefined);

  // Only preload if we have a different URL and displayedImageUrl is set (not first load)
  const shouldPreload = isImage && currentImageUrl && displayedImageUrl && currentImageUrl !== displayedImageUrl;

  // Format dates
  const uploadedAt = file.record?.originalData?.created_at || file.createdAt || file.lastUpdatedAt;
  const uploadedDate = uploadedAt ? DateHelper.format(new Date(uploadedAt), "MM/DD/YYYY") : "";
  const lastUpdatedDate = file.lastUpdatedAt ? DateHelper.format(new Date(file.lastUpdatedAt), "MM/DD/YYYY") : "";

  // Get source
  const sourceLabel = file.isCCImage ? "CompanyCam" : "Swivu";

  // Get age
  const age = file.record?.aging || file.age || "";

  // Build tabs array based on file type
  const tabs = [
    {
      value: "details",
      label: "Details",
      icon: FileText,
      iconColor: "text-purple-500",
      iconBg: "bg-purple-500/10",
      content: (
        <div className="p-6 space-y-6 overflow-y-auto">
          {/* File Name */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                File Name
              </label>
              <p className="text-sm text-foreground font-medium">{file.name || "N/A"}</p>
            </div>

            {/* Source */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" />
                Source
              </label>
              <p className="text-sm text-foreground font-medium">{sourceLabel}</p>
            </div>
          </div>

          {/* File Size & Age */}
          {(file.size && file.size !== "N/A") || age ? (
            <div className="grid grid-cols-2 gap-6">
              {file.size && file.size !== "N/A" && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <HardDrive className="h-3.5 w-3.5" />
                    File Size
                  </label>
                  <p className="text-sm text-foreground font-medium">{file.size}</p>
                </div>
              )}

              {age && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    Age
                  </label>
                  <p className="text-sm text-foreground font-medium">{age}</p>
                </div>
              )}
            </div>
          ) : null}

          {/* Uploaded Date & Uploaded By */}
          {(uploadedDate || file.addedBy) && (
            <div className="grid grid-cols-2 gap-6">
              {uploadedDate && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Uploaded Date
                  </label>
                  <p className="text-sm text-foreground font-medium">{uploadedDate}</p>
                </div>
              )}

              {file.addedBy && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    Uploaded By
                  </label>
                  <p className="text-sm text-foreground font-medium">{file.addedBy}</p>
                </div>
              )}
            </div>
          )}

          {/* Last Updated Date & Last Updated By */}
          {(lastUpdatedDate || file.lastUpdatedBy) && (
            <div className="grid grid-cols-2 gap-6">
              {lastUpdatedDate && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Last Updated Date
                  </label>
                  <p className="text-sm text-foreground font-medium">{lastUpdatedDate}</p>
                </div>
              )}

              {file.lastUpdatedBy && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    Last Updated By
                  </label>
                  <p className="text-sm text-foreground font-medium">{file.lastUpdatedBy}</p>
                </div>
              )}
            </div>
          )}

          {/* Description (Editable) */}
          <div className="pt-4 border-t border-border/50">
            <FileDescriptionSection />
          </div>
        </div>
      )
    },
    ...(isImage && !file.isCCImage
      ? [
          {
            value: "findings",
            label: "Key Findings",
            icon: MapPin,
            iconColor: "text-orange-500",
            iconBg: "bg-orange-500/10",
            content: (
              <KeyFindingsTable />
            )
          }
        ]
      : []),
    ...(isImage
      ? [
          {
            value: "points",
            label: "Points",
            icon: Award,
            iconColor: "text-blue-500",
            iconBg: "bg-blue-500/10",
            content: <IncentivePointsSection />
          }
        ]
      : []),
    {
      value: "share",
      label: "Share & Permissions",
      icon: Share2,
      iconColor: "text-green-500",
      iconBg: "bg-green-500/10",
      content: (
        <div className="overflow-y-auto">
          <SharePermissionsSection />
        </div>
      )
    }
  ];

  return (
    <div className="flex-1 h-full min-h-0 bg-muted/20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 h-full">
        {/* Left Column - File Preview */}
        <div className="h-full flex flex-col gap-6 overflow-hidden">
          <div className="rounded-xl border border-border/50 bg-card flex flex-col h-full overflow-hidden shadow-sm">
            <div className="p-4 border-b border-border/50 flex items-center gap-2 flex-shrink-0 bg-muted/30">
              <div className="p-1.5 rounded-md bg-primary/10">
                <ImageIcon className="h-4 w-4 text-primary" />
              </div>
              <h3 className="text-sm font-semibold text-foreground">File Preview</h3>
            </div>
            <div className="p-4 flex-1 min-h-0 overflow-hidden">
              <div className="relative h-full w-full flex items-center justify-center rounded-lg border border-border/50 bg-muted/30 overflow-hidden">
                {/* Display file preview - shows the displayedImageUrl (previous image while loading new one) */}
                <FilePreview
                  file={file}
                  src={srcToDisplay}
                  className="relative z-10 max-h-full max-w-full object-contain"
                  fileIconClassName="size-24"
                  showName={false}
                  disableLoadingState={true}
                  enableVideoAudioRendering={true}
                />

                {/* Preload new image invisibly - only when navigating (not first load) */}
                {shouldPreload && <img src={currentImageUrl} alt="" className="hidden" onLoad={handleLoadComplete} onError={handleLoadComplete} />}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Tabs */}
        <div className="flex flex-col h-full overflow-hidden">
          <Tabs value={summaryTab} onValueChange={(value) => setSummaryTab(value as "details" | "findings" | "points" | "share")} className="h-full flex flex-col">
            <TabsList className="w-full justify-start h-auto p-1.5 bg-muted/50 rounded-xl mb-4 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background"
                    activeElementClassName={`${tab.iconBg} !bg-transparent`}
                  >
                    <div className={`p-1.5 rounded-md ${tab.iconBg}`}>
                      <Icon className={`h-3.5 w-3.5 ${tab.iconColor}`} />
                    </div>
                    <span className="text-sm font-medium">{tab.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
              {tabs.map((tab) => (
                <TabsContent key={tab.value} value={tab.value} className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                  {tab.content}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
